import React from 'react'
import { Box } from '@mui/material'
import { LayoutWrapper, ContentWrapper, SectionWrapper } from './ReportDesignerContainer.styles'
import { ReportDesignerHeader } from '../../../components/report-templates/ReportDesignerHeader'
import { SchemaSelectionSection } from '../../../components/report-templates/SchemaSelectionSection'
import { ComponentLibrarySection } from '../../../components/report-templates/ComponentLibrarySection'
import { DesignerCanvasSection } from '../../../components/report-templates/DesignerCanvasSection'
import { PropertyConfigurationSection } from '../../../components/report-templates/PropertyConfigurationSection'
import { ShowDialogProps } from 'src/show-dialog'
import { IDropdownDto } from 'src/generated-types/api-result'
import { Spinner } from 'src/components/Spinner'
import { GenericDialogLayout } from 'src/components/GenericDialogLayout'
import { useReportDesigner } from './useReportDesigner'

export interface IReportDesignerContainerProps extends ShowDialogProps {
  customerId: number
  formSchema?: IDropdownDto
  templateName?: string
  locations?: number[]
  templateId?: EntityId
}

export const ReportDesignerContainer: React.FC<IReportDesignerContainerProps> = ({
  customerId,
  formSchema,
  templateName,
  locations,
  templateId,
  pop,
}) => {
  const {
    canvasItems,
    selectedItemId,
    currentTemplateName,
    currentLocations,
    setCurrentTemplateName,
    setCurrentLocations,
    initialData,
    isTemplateLoading,
    formSchemaColumns,
    isColumnsLoading,
    warnings,
    isPreviewLoading,
    selectedItem,
    hasTopBar,
    warningMessage,
    isEditMode,
    handlePreview,
    handleSaveDraft,
    handleSaveReport,
    handleCanvasDrop,
    handleCanvasDropAtIndex,
    handleSelectItem,
    handleUpdateItem,
    handleReorder,
    handleDeleteItem,
    handleStatusChange,
    handleUpgrade,
    handleLookUpChange,
    isUpgradeLoading,
  } = useReportDesigner({ customerId, templateId, templateName, locations, formSchema })

  const renderHeaderContent = () => (
    <ReportDesignerHeader
      onPreview={handlePreview}
      onSaveDraft={handleSaveDraft}
      onSaveReport={handleSaveReport}
      onClose={pop}
      isEditMode={isEditMode}
      status={initialData?.status}
      handleChangeStatus={handleStatusChange}
      onUpgrade={handleUpgrade}
      isPreviewLoading={isPreviewLoading}
      warningMessage={warningMessage}
      isUpgradeLoading={isUpgradeLoading}
      warnings={warnings}
    />
  )

  return (
    <GenericDialogLayout
      renderCustomHeader={renderHeaderContent}
      removedCloseButton={true}
      sx={{ padding: 0 }}
      dialogActionsSx={{ padding: 0 }}
    >
      {templateId && (isTemplateLoading || !initialData) ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="400px">
          <Spinner />
        </Box>
      ) : (
        <LayoutWrapper>
          <ContentWrapper>
            <SectionWrapper sx={{ flex: 1, minHeight: 0 }}>
              <SchemaSelectionSection formSchema={formSchema} />
              <ComponentLibrarySection hasTopBar={hasTopBar} />
            </SectionWrapper>
            <SectionWrapper sx={{ height: '800px', overflow: 'auto' }}>
              <DesignerCanvasSection
                canvasItems={canvasItems}
                selectedItemId={selectedItemId}
                columns={formSchemaColumns}
                onSelectItem={handleSelectItem}
                onDrop={handleCanvasDrop}
                onDropAtIndex={handleCanvasDropAtIndex}
                onReorder={handleReorder}
                onDeleteItem={handleDeleteItem}
              />
            </SectionWrapper>
            <SectionWrapper>
              <PropertyConfigurationSection
                selectedItem={selectedItem}
                onUpdateItem={handleUpdateItem}
                columns={formSchemaColumns}
                isColumnsLoading={isColumnsLoading}
                templateName={currentTemplateName}
                locations={currentLocations}
                status={initialData?.status}
                onTemplateNameChange={setCurrentTemplateName}
                onLocationsChange={setCurrentLocations}
                customerId={customerId}
                isEditMode={isEditMode}
                templateId={initialData?.id}
                formSchemaVersionId={initialData?.formSchemaVersionId}
                onLookUpChange={handleLookUpChange}
                warnings={warnings}
                warningMessage={warningMessage}
                onUpgrade={handleUpgrade}
                isUpgradeLoading={isUpgradeLoading}
              />
            </SectionWrapper>
          </ContentWrapper>
        </LayoutWrapper>
      )}
    </GenericDialogLayout>
  )
}
